import * as Yup from 'yup';

import { NotificationButtonElement } from '../../../../../../models/formula.model';
import { IosCategoryIdentifier } from '../../../../../../models/notificationButton.model';

export enum NotificationAndroidActionType {
    DEEP_LINK = 'deep_link',
    OPEN_APP = 'open_app',
    URI = 'uri',
    CLOSE = 'close',
}

export enum PlatformType {
    ANDROID = 'android',
    IOS = 'ios',
}

export const getValidationSchema = Yup.object().shape({
    iosToggle: Yup.boolean(),
    androidToggle: Yup.boolean(),
    iosButtons: Yup.array().when('iosToggle', {
        is: true,
        then: Yup.array().of(
            Yup.object().shape({
                buttonText: Yup.string(),
                actionType: Yup.string(),
                uri: Yup.string()
                    .url('Must be a valid URL')
                    .when('action', {
                        is: (action: any) =>
                            [
                                NotificationAndroidActionType.DEEP_LINK,
                                NotificationAndroidActionType.URI,
                            ].includes(action),
                        then: Yup.string().required(
                            'URI is required for deep link',
                        ),
                        otherwise: Yup.string().notRequired().nullable(),
                    }),
            }),
        ),
        otherwise: Yup.array().nullable(),
    }),
    androidButtons: Yup.array().when('androidToggle', {
        is: true,
        then: Yup.array().of(
            Yup.object().shape({
                text: Yup.string()
                    .trim()
                    .required('Button text is required')
                    .test(
                        'uniqueButtonText',
                        'Button text must be unique',
                        function (value, context) {
                            const { androidButtons } = context.options
                                .context as any;
                            const buttonTexts = androidButtons?.map(
                                (button: any) => button.text,
                            );
                            return (
                                buttonTexts?.filter(
                                    (text: string) =>
                                        text?.trim() === value?.trim(),
                                ).length === 1
                            );
                        },
                    ),
                action: Yup.string().required('Required'),
                uri: Yup.string()
                    .url('Must be a valid URL')
                    .when('action', {
                        is: (action: any) =>
                            [
                                NotificationAndroidActionType.DEEP_LINK,
                                NotificationAndroidActionType.URI,
                            ].includes(action),
                        then: Yup.string().required(
                            'URI is required for deep link',
                        ),
                        otherwise: Yup.string().notRequired().nullable(),
                    }),
            }),
        ),
        otherwise: Yup.array().nullable(),
    }),
});

export const androidAddButtonElement: NotificationButtonElement = {
    text: '',
    action: NotificationAndroidActionType.OPEN_APP,
    uri: '',
    platform: PlatformType.ANDROID,
};

export const NotificationAndroidOptions = [
    {
        label: 'Deep link',
        value: NotificationAndroidActionType.DEEP_LINK,
    },
    {
        label: 'Open App',
        value: NotificationAndroidActionType.OPEN_APP,
    },
    {
        label: 'URI',
        value: NotificationAndroidActionType.URI,
    },
    {
        label: 'Close',
        value: NotificationAndroidActionType.CLOSE,
    },
];

export const IosCategoryIdentifierOptions = [
    {
        label: 'Accept/Decline',
        value: IosCategoryIdentifier.ACCEPT_DECLINE,
    },
    {
        label: 'Yes/No',
        value: IosCategoryIdentifier.YES_NO,
    },
    {
        label: 'Confirm/Cancel',
        value: IosCategoryIdentifier.CONFIRM_CANCEL,
    },
    {
        label: 'More',
        value: IosCategoryIdentifier.MORE,
    },
];

export enum IosActionId {
    YES = 'ab_pb_yes',
    NO = 'ab_pb_no',
    ACCEPT = 'ab_pb_accept',
    DECLINE = 'ab_pb_decline',
    CONFIRM = 'ab_pb_confirm',
    CANCEL = 'ab_pb_cancel',
    MORE = 'ab_pb_more',
}

export enum IosButtonTextAction {
    YES = 'Yes',
    NO = 'No',
    ACCEPT = 'Accept',
    DECLINE = 'Decline',
    CONFIRM = 'Confirm',
    CANCEL = 'Cancel',
    MORE = 'More',
}

export const iosButtonFields: {
    [key in IosCategoryIdentifier]: NotificationButtonElement[];
} = {
    [IosCategoryIdentifier.YES_NO]: [
        {
            action_id: IosActionId.YES,
            action: NotificationAndroidActionType.OPEN_APP,
            text: IosButtonTextAction.YES,
            platform: PlatformType.IOS,
        },
        {
            action_id: IosActionId.NO,
            action: NotificationAndroidActionType.CLOSE,
            text: IosButtonTextAction.NO,
            platform: PlatformType.IOS,
        },
    ],
    [IosCategoryIdentifier.ACCEPT_DECLINE]: [
        {
            action_id: IosActionId.ACCEPT,
            action: NotificationAndroidActionType.OPEN_APP,
            text: IosButtonTextAction.ACCEPT,
            platform: PlatformType.IOS,
        },
        {
            action_id: IosActionId.DECLINE,
            action: NotificationAndroidActionType.CLOSE,
            text: IosButtonTextAction.DECLINE,
            platform: PlatformType.IOS,
        },
    ],
    [IosCategoryIdentifier.CONFIRM_CANCEL]: [
        {
            action_id: IosActionId.CONFIRM,
            action: NotificationAndroidActionType.OPEN_APP,
            text: IosButtonTextAction.CONFIRM,
            platform: PlatformType.IOS,
        },
        {
            action_id: IosActionId.CANCEL,
            action: NotificationAndroidActionType.CLOSE,
            text: IosButtonTextAction.CANCEL,
            platform: PlatformType.IOS,
        },
    ],
    [IosCategoryIdentifier.MORE]: [
        {
            action_id: IosActionId.MORE,
            action: NotificationAndroidActionType.OPEN_APP,
            text: IosButtonTextAction.MORE,
            platform: PlatformType.IOS,
        },
    ],
};

export enum ButtonType {
    SUBMIT = 'submit',
    BUTTON = 'button',
}

export const ModalDescription =
    'Enable buttons for iOS or Android or both. If you enable just one, ensure your message works with or without buttons.';

export enum ButtonPopupType {
    EDIT = 'Edit buttons',
    Delete = 'Delete buttons',
}
